/**
 * Text Highlighter - Smart text highlighting with date pattern priority
 * 
 * Highlights search matches in text with priority:
 * 1. Date patterns (from smart-date-recognizer) - highest priority
 * 2. Text patterns (from query parser) - lower priority
 */

"use strict";

import { extractPatterns } from './file-matcher/condition-utils.js';

// Check if debug mode is enabled
const urlParams = new URLSearchParams(window.location.search);
const debugMode = urlParams.get('debug') || '0';

// Cache for deduplication - avoid logging same query multiple times
let lastHighlightedQuery = null;

/**
 * Generate date highlight patterns for text highlighting (not search)
 * Uses smart-date-recognizer to generate patterns suitable for filename highlighting
 * @param {string} query - Search query
 * @returns {Array} Array of date patterns for highlighting
 */
function generateDateHighlightPatterns(query) {
    if (!query || typeof query !== 'string') return [];
    
    const patterns = [];
    
    // Use smart-date-recognizer if available
    if (typeof window.recognizeDate === 'function') {
        const recognized = window.recognizeDate(query);
        if (recognized) {
            // Generate text patterns for highlighting (not YYYY-MM-DD format)
            const usedTokens = recognized.usedTokens || [];
            
            // CRITICAL: Check if this is YYMM[A-Z]DD format (e.g., "5311C26")
            // This format has a letter separator between month and day
            // Check query directly for YYMM[A-Z]DD pattern
            const yymmLetterDdMatch = query.match(/(\d{2})(\d{2})([A-Za-z])(\d{2})/);
            const hasLetterSeparator = yymmLetterDdMatch !== null;
            
            // Add patterns for date tokens (day, month names, year)
            for (const token of usedTokens) {
                const tokenLower = token.toLowerCase();
                // Escape special regex characters
                const escapedToken = tokenLower.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
                
                // For numeric tokens (day, year), add multiple formats
                if (/^\d+$/.test(token)) {
                    const num = parseInt(token, 10);
                    // Add as-is (for standalone numbers)
                    patterns.push({
                        pattern: `\\b${escapedToken}\\b`,
                        flags: 'i',
                        priority: 1
                    });
                    // Add without leading zero if applicable
                    if (num < 10 && token.startsWith('0')) {
                        patterns.push({
                            pattern: `\\b${num}\\b`,
                            flags: 'i',
                            priority: 1
                        });
                    }
                } else {
                    // For text tokens (month names), add with and without word boundaries
                    patterns.push({
                        pattern: `\\b${escapedToken}\\b`,
                        flags: 'i',
                        priority: 1
                    });
                    patterns.push({
                        pattern: escapedToken,
                        flags: 'i',
                        priority: 1
                    });
                }
            }
            
            // CRITICAL: Generate patterns for YYMM[A-Z]DD format if detected in query
            // This handles cases like "5311C26" where the query itself contains the letter separator
            if (hasLetterSeparator && yymmLetterDdMatch) {
                const yy = yymmLetterDdMatch[1]; // "53"
                const mm = yymmLetterDdMatch[2]; // "11"
                const letter = yymmLetterDdMatch[3]; // "C"
                const dd = yymmLetterDdMatch[4]; // "26"
                
                // Check if this matches recognized date
                const recognizedYear = recognized.from ? recognized.from.split('-')[0] : null;
                const recognizedMonth = recognized.from ? recognized.from.split('-')[1] : null;
                const recognizedDay = recognized.from ? recognized.from.split('-')[2] : null;
                
                // If recognized date matches YYMM[A-Z]DD format, generate patterns
                if (recognizedYear && recognizedMonth && recognizedDay) {
                    const shortYear = recognizedYear.slice(-2);
                    if (shortYear === yy && recognizedMonth === mm && recognizedDay === dd) {
                        patterns.push(
                            // YYMM[A-Z]DD format: "5311C26" = 26 Nov 1953/2053
                            { pattern: `${yy}${mm}${letter}${dd}`, flags: 'i', priority: 2 }, // 5311C26 (highest priority for exact match)
                            { pattern: `${recognizedYear}${mm}${letter}${dd}`, flags: 'i', priority: 2 }, // 195311C26 (highest priority)
                            // Generic pattern with any letter (for flexibility)
                            { pattern: `${yy}${mm}[A-Za-z]${dd}`, flags: 'i', priority: 1 }, // 5311C26 (any letter)
                            { pattern: `${recognizedYear}${mm}[A-Za-z]${dd}`, flags: 'i', priority: 1 }, // 195311C26 (any letter)
                            // MM[A-Z]DD format (without year)
                            { pattern: `${mm}${letter}${dd}`, flags: 'i', priority: 1 }, // 11C26
                            { pattern: `${mm}[A-Za-z]${dd}`, flags: 'i', priority: 1 }, // 11C26 (any letter)
                            // Individual components
                            { pattern: `\\b${yy}\\b`, flags: 'i', priority: 1 }, // "53"
                            { pattern: `\\b${mm}\\b`, flags: 'i', priority: 1 }, // "11"
                            { pattern: `\\b${dd}\\b`, flags: 'i', priority: 1 }, // "26"
                            { pattern: yy, flags: 'i', priority: 1 },
                            { pattern: mm, flags: 'i', priority: 1 },
                            { pattern: dd, flags: 'i', priority: 1 }
                        );
                    }
                } else {
                    // Even if not recognized as full date, generate patterns for YYMM[A-Z]DD format
                    patterns.push(
                        { pattern: `${yy}${mm}${letter}${dd}`, flags: 'i', priority: 2 }, // 5311C26 (highest priority)
                        { pattern: `${yy}${mm}[A-Za-z]${dd}`, flags: 'i', priority: 1 }, // 5311C26 (any letter)
                        { pattern: `${mm}${letter}${dd}`, flags: 'i', priority: 1 }, // 11C26
                        { pattern: `${mm}[A-Za-z]${dd}`, flags: 'i', priority: 1 }, // 11C26 (any letter)
                        { pattern: `\\b${yy}\\b`, flags: 'i', priority: 1 },
                        { pattern: `\\b${mm}\\b`, flags: 'i', priority: 1 },
                        { pattern: `\\b${dd}\\b`, flags: 'i', priority: 1 },
                        { pattern: yy, flags: 'i', priority: 1 },
                        { pattern: mm, flags: 'i', priority: 1 },
                        { pattern: dd, flags: 'i', priority: 1 }
                    );
                }
            }
            
            // Generate numeric date patterns
            // CRITICAL: Check for exact date (from === to) OR month-year format (from !== to, but same month)
            if (recognized.from && recognized.to) {
                const fromParts = recognized.from.split('-');
                const toParts = recognized.to.split('-');
                
                if (fromParts.length === 3 && toParts.length === 3) {
                    const fromYear = fromParts[0];
                    const fromMonth = fromParts[1];
                    const fromDay = fromParts[2];
                    const toYear = toParts[0];
                    const toMonth = toParts[1];
                    const toDay = toParts[2];
                    
                    // Check if this is month-year format (same year and month, different days)
                    const isMonthYearFormat = fromYear === toYear && fromMonth === toMonth && fromDay !== toDay;
                    // Check if this is exact date (all parts equal)
                    const isExactDate = fromYear === toYear && fromMonth === toMonth && fromDay === toDay;
                    
                    if (isExactDate || isMonthYearFormat) {
                        const year = fromYear;
                        const month = fromMonth;
                        const day = isExactDate ? fromDay : null; // null for month-year format
                        const shortYear = year.slice(-2); // "53" for 1953
                        
                        // Add patterns: DD.MM.YYYY, DD-MM-YY, DD/MM/YYYY, etc.
                        const separators = '[\\s\\-\\./,]';
                        const letterSeparators = '[CcMm]';
                        
                        if (isExactDate && day) {
                            // Full date formats (exact date)
                            patterns.push(
                                // Full date formats
                                { pattern: `${day}${separators}${month}${separators}${year}`, flags: 'i', priority: 1 },
                                { pattern: `${day}${separators}${month}${separators}${shortYear}`, flags: 'i', priority: 1 },
                                { pattern: `${year}${separators}${month}${separators}${day}`, flags: 'i', priority: 1 },
                                // Day-month formats
                                { pattern: `${day}${separators}${month}`, flags: 'i', priority: 1 },
                                { pattern: `${month}${separators}${day}`, flags: 'i', priority: 1 },
                                // Compact formats (no separators): YYMMDD, DDMMYY, MMDD
                                { pattern: `${shortYear}${month}${day}`, flags: 'i', priority: 1 }, // 531115
                                { pattern: `${day}${month}${shortYear}`, flags: 'i', priority: 1 }, // 151153
                                { pattern: `${month}${day}`, flags: 'i', priority: 1 }, // 1115
                                // Special filename formats with letter separator (C/M): YYMM[A-Z]DD, MMC18
                                { pattern: `${shortYear}${month}${letterSeparators}${day}`, flags: 'i', priority: 2 }, // 5311C26 (highest priority for exact match)
                                { pattern: `${year}${month}${letterSeparators}${day}`, flags: 'i', priority: 2 }, // 195311C26 (highest priority for exact match)
                                { pattern: `${month}${letterSeparators}${day}`, flags: 'i', priority: 1 }, // 11C26
                                // Also add pattern with any letter (for flexibility)
                                { pattern: `${shortYear}${month}[A-Za-z]${day}`, flags: 'i', priority: 1 }, // 5311C26 (any letter)
                                { pattern: `${year}${month}[A-Za-z]${day}`, flags: 'i', priority: 1 }, // 195311C26 (any letter)
                                // Year patterns
                                { pattern: year, flags: 'i', priority: 1 },
                                { pattern: shortYear, flags: 'i', priority: 1 } // Short year (e.g., "53" for 1953)
                            );
                        } else if (isMonthYearFormat) {
                            // Month-year format (e.g., "53 ноя" = November 1953, "5311" = November 1953)
                            // Generate patterns for year-month combinations
                            // Use usedTokens to get actual month name from query (e.g., "ноя", "nov", "november")
                            const monthToken = usedTokens.find(t => !/^\d+$/.test(t)); // Find non-numeric token (month name)
                            const yearToken = usedTokens.find(t => /^\d+$/.test(t)); // Find numeric token (year)
                            
                            // CRITICAL: For 4-digit compact format like "5311", usedTokens might be ['53', '11']
                            // In this case, we need to generate patterns for both YYMM format and individual tokens
                            const isCompact4Digit = usedTokens.length === 2 && 
                                                   usedTokens.every(t => /^\d{2}$/.test(t)) &&
                                                   query.trim().match(/^\d{4}/);
                            
                            // Check if both tokens are numeric (compact format like "5311")
                            const numericTokens = usedTokens.filter(t => /^\d+$/.test(t));
                            const isNumericCompact = numericTokens.length === 2 && isMonthYearFormat;
                            
                            // For numeric compact format (e.g., "5311"), generate patterns directly
                            if (isNumericCompact && numericTokens.length === 2) {
                                const firstToken = numericTokens[0]; // "53"
                                const secondToken = numericTokens[1]; // "11"
                                const compactPattern = `${firstToken}${secondToken}`; // "5311"
                                
                                patterns.push(
                                    // The compact format itself (highest priority for exact match)
                                    { pattern: compactPattern, flags: 'i', priority: 2 }, // "5311"
                                    // Individual components (for highlighting "53" and "11" separately)
                                    { pattern: `\\b${firstToken}\\b`, flags: 'i', priority: 1 }, // "53"
                                    { pattern: `\\b${secondToken}\\b`, flags: 'i', priority: 1 }, // "11"
                                    { pattern: firstToken, flags: 'i', priority: 1 }, // "53" without word boundaries
                                    { pattern: secondToken, flags: 'i', priority: 1 }, // "11" without word boundaries
                                    // With separators
                                    { pattern: `${firstToken}${separators}${secondToken}`, flags: 'i', priority: 1 }, // "53-11", "53 11"
                                    { pattern: `${secondToken}${separators}${firstToken}`, flags: 'i', priority: 1 }, // "11-53", "11 53"
                                    // Full year formats (if we know the year)
                                    { pattern: `${year}${month}`, flags: 'i', priority: 1 }, // "195311"
                                    { pattern: `${month}${year}`, flags: 'i', priority: 1 }, // "111953"
                                    { pattern: `\\b${year}\\b`, flags: 'i', priority: 1 },
                                    { pattern: year, flags: 'i', priority: 1 }
                                );
                            } else if (monthToken && yearToken) {
                                // Text format: "53 ноя", "1953 ноя"
                                const escapedMonth = monthToken.toLowerCase().replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
                                const escapedYear = yearToken.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
                                
                                patterns.push(
                                    // Year-Month text formats: "53 ноя", "1953 ноя", "53 ноябрь", "1953 november"
                                    // CRITICAL: These patterns match the actual query format
                                    { pattern: `\\b${escapedYear}${separators}${escapedMonth}\\b`, flags: 'i', priority: 1 },
                                    { pattern: `\\b${escapedMonth}${separators}${escapedYear}\\b`, flags: 'i', priority: 1 },
                                    // Without word boundaries (for filenames like "5311C03B" where "53" and "11" are adjacent)
                                    { pattern: `${escapedYear}${separators}${escapedMonth}`, flags: 'i', priority: 1 },
                                    { pattern: `${escapedMonth}${separators}${escapedYear}`, flags: 'i', priority: 1 },
                                    // Compact numeric formats: YYMM, YYYYMM (for filenames like "5311C03B")
                                    { pattern: `${shortYear}${month}`, flags: 'i', priority: 1 }, // 5311
                                    { pattern: `${year}${month}`, flags: 'i', priority: 1 }, // 195311
                                    { pattern: `${month}${shortYear}`, flags: 'i', priority: 1 }, // 1153
                                    { pattern: `${month}${year}`, flags: 'i', priority: 1 }, // 111953
                                    // Year patterns (standalone)
                                    { pattern: `\\b${year}\\b`, flags: 'i', priority: 1 },
                                    { pattern: `\\b${shortYear}\\b`, flags: 'i', priority: 1 }, // Short year (e.g., "53" for 1953)
                                    { pattern: year, flags: 'i', priority: 1 },
                                    { pattern: shortYear, flags: 'i', priority: 1 }
                                );
                            } else if (isNumericCompact || isCompact4Digit) {
                                // 4-digit compact format: "5311" = YYMM (53-11 = November 1953)
                                // Generate patterns for the compact format itself
                                const compactPattern = `${shortYear}${month}`; // "5311"
                                patterns.push(
                                    // The compact format itself (highest priority for exact match)
                                    { pattern: compactPattern, flags: 'i', priority: 2 }, // "5311"
                                    // Individual components (for highlighting "53" and "11" separately)
                                    { pattern: `\\b${shortYear}\\b`, flags: 'i', priority: 1 }, // "53"
                                    { pattern: `\\b${month}\\b`, flags: 'i', priority: 1 }, // "11"
                                    { pattern: shortYear, flags: 'i', priority: 1 }, // "53" without word boundaries
                                    { pattern: month, flags: 'i', priority: 1 }, // "11" without word boundaries
                                    // With separators
                                    { pattern: `${shortYear}${separators}${month}`, flags: 'i', priority: 1 }, // "53-11", "53 11"
                                    { pattern: `${month}${separators}${shortYear}`, flags: 'i', priority: 1 }, // "11-53", "11 53"
                                    // Full year formats
                                    { pattern: `${year}${month}`, flags: 'i', priority: 1 }, // "195311"
                                    { pattern: `${month}${year}`, flags: 'i', priority: 1 }, // "111953"
                                    { pattern: `\\b${year}\\b`, flags: 'i', priority: 1 },
                                    { pattern: year, flags: 'i', priority: 1 }
                                );
                            }
                        }
                    }
                }
            }
            
            // Handle variants (day-month without year, ambiguous dates)
            if (recognized.variants && Array.isArray(recognized.variants)) {
                for (const variant of recognized.variants) {
                    if (variant.pattern) {
                        // Extract day and month from pattern like \d{4}-11-15
                        const match = variant.pattern.match(/\\d\{4\}-(\d{2})-(\d{2})/);
                        if (match) {
                            const month = match[1];
                            const day = match[2];
                            const separators = '[\\s\\-\\./,]';
                            patterns.push(
                                { pattern: `${day}${separators}${month}`, flags: 'i', priority: 1 },
                                { pattern: `${month}${separators}${day}`, flags: 'i', priority: 1 },
                                // Compact format without separators
                                { pattern: `${day}${month}`, flags: 'i', priority: 1 }, // 1115
                                { pattern: `${month}${day}`, flags: 'i', priority: 1 }  // 1115 (reversed)
                            );
                        }
                        // Handle exact date patterns (for ambiguous dates)
                        const exactMatch = variant.pattern.match(/^(\d{4})-(\d{2})-(\d{2})$/);
                        if (exactMatch) {
                            const year = exactMatch[1];
                            const month = exactMatch[2];
                            const day = exactMatch[3];
                            const shortYear = year.slice(-2);
                            const separators = '[\\s\\-\\./,]';
                            patterns.push(
                                { pattern: `${day}${separators}${month}${separators}${year}`, flags: 'i', priority: 1 },
                                { pattern: `${day}${separators}${month}${separators}${shortYear}`, flags: 'i', priority: 1 },
                                { pattern: `${year}${separators}${month}${separators}${day}`, flags: 'i', priority: 1 },
                                { pattern: `${day}${separators}${month}`, flags: 'i', priority: 1 },
                                { pattern: `${month}${separators}${day}`, flags: 'i', priority: 1 },
                                { pattern: `${shortYear}${month}${day}`, flags: 'i', priority: 1 },
                                { pattern: `${day}${month}${shortYear}`, flags: 'i', priority: 1 },
                                { pattern: `${month}${day}`, flags: 'i', priority: 1 }
                            );
                        }
                    }
                }
            }
        }
    }
    
    // CRITICAL: Also handle 4-digit numbers that might be dates even if not recognized
    // This ensures "1118" gets highlight patterns even if ambiguous
    if (patterns.length === 0 || /^\d{4}$/.test(query.trim())) {
        const qTrimmed = query.trim();
        const compact4Match = qTrimmed.match(/^(\d{4})$/);
        if (compact4Match) {
            const digits = compact4Match[1];
            const num1 = parseInt(digits.substring(0, 2), 10);
            const num2 = parseInt(digits.substring(2, 4), 10);
            
            // Generate patterns for all possible interpretations
            const separators = '[\\s\\-\\./,]';
            
            // Interpretation 1: Day-Month (11-18 = 11 ноября или 18 ноября)
            if (num1 >= 1 && num1 <= 31 && num2 >= 1 && num2 <= 12) {
                const day = String(num1).padStart(2, '0');
                const month = String(num2).padStart(2, '0');
                patterns.push(
                    { pattern: `${day}${separators}${month}`, flags: 'i', priority: 1 },
                    { pattern: `${month}${separators}${day}`, flags: 'i', priority: 1 },
                    { pattern: `${day}${month}`, flags: 'i', priority: 1 }, // 1118
                    { pattern: `${month}${day}`, flags: 'i', priority: 1 }  // 1811
                );
            }
            
            // Interpretation 2: Month-Day (11-18 = ноябрь 18)
            if (num1 >= 1 && num1 <= 12 && num2 >= 1 && num2 <= 31 && num1 !== num2) {
                const month = String(num1).padStart(2, '0');
                const day = String(num2).padStart(2, '0');
                patterns.push(
                    { pattern: `${month}${separators}${day}`, flags: 'i', priority: 1 },
                    { pattern: `${day}${separators}${month}`, flags: 'i', priority: 1 },
                    { pattern: `${month}${day}`, flags: 'i', priority: 1 }, // 1118
                    { pattern: `${day}${month}`, flags: 'i', priority: 1 }  // 1811
                );
            }
            
            // Interpretation 3: Month-Year (11-18 = ноябрь 2018)
            if (num1 >= 1 && num1 <= 12) {
                const month = String(num1).padStart(2, '0');
                const year = num2 >= 50 ? `19${String(num2).padStart(2, '0')}` : `20${String(num2).padStart(2, '0')}`;
                const shortYear = String(num2).padStart(2, '0');
                patterns.push(
                    { pattern: `${month}${separators}${year}`, flags: 'i', priority: 1 },
                    { pattern: `${month}${separators}${shortYear}`, flags: 'i', priority: 1 },
                    { pattern: `${year}${separators}${month}`, flags: 'i', priority: 1 },
                    { pattern: `${shortYear}${separators}${month}`, flags: 'i', priority: 1 },
                    { pattern: `${month}${year}`, flags: 'i', priority: 1 }, // 112018
                    { pattern: `${year}${month}`, flags: 'i', priority: 1 }, // 201811
                    { pattern: `${month}${shortYear}`, flags: 'i', priority: 1 }, // 1118
                    { pattern: `${shortYear}${month}`, flags: 'i', priority: 1 }  // 1811
                );
            }
            
            // Interpretation 4: Year-Month (11-18 = ноябрь 2011)
            if (num2 >= 1 && num2 <= 12) {
                const year = num1 >= 50 ? `19${String(num1).padStart(2, '0')}` : `20${String(num1).padStart(2, '0')}`;
                const shortYear = String(num1).padStart(2, '0');
                const month = String(num2).padStart(2, '0');
                patterns.push(
                    { pattern: `${year}${separators}${month}`, flags: 'i', priority: 1 },
                    { pattern: `${shortYear}${separators}${month}`, flags: 'i', priority: 1 },
                    { pattern: `${month}${separators}${year}`, flags: 'i', priority: 1 },
                    { pattern: `${month}${separators}${shortYear}`, flags: 'i', priority: 1 },
                    { pattern: `${year}${month}`, flags: 'i', priority: 1 }, // 201118
                    { pattern: `${shortYear}${month}`, flags: 'i', priority: 1 }, // 1118
                    { pattern: `${month}${year}`, flags: 'i', priority: 1 }, // 112011
                    { pattern: `${month}${shortYear}`, flags: 'i', priority: 1 }  // 1111
                );
            }
            
            // Always add the original 4-digit pattern for exact match
            patterns.push({
                pattern: digits,
                flags: 'i',
                priority: 2 // Higher priority for exact match
            });
            
            // CRITICAL: Add patterns for special filename formats with letter separator
            // Formats like "11C18" (MMC18), "11M18" (MMM18)
            // Where C/M is a letter separator between month and day
            // IMPORTANT: Only generate patterns that match the exact query "1118", not wildcard patterns like \d{2}11C18
            const letterSeparators = '[CcMm]'; // C or M (common in filenames)
            
            // For day-month interpretation: generate MMC18 format (11C18 = ноябрь 18)
            if (num1 >= 1 && num1 <= 12 && num2 >= 1 && num2 <= 31) {
                const month = String(num1).padStart(2, '0');
                const day = String(num2).padStart(2, '0');
                patterns.push(
                    { pattern: `${month}${letterSeparators}${day}`, flags: 'i', priority: 1 } // 11C18 (only exact match)
                );
            }
            
            // For month-year interpretation: generate MMCYY format (11C18 = ноябрь 2018)
            if (num1 >= 1 && num1 <= 12) {
                const month = String(num1).padStart(2, '0');
                const shortYear = String(num2).padStart(2, '0');
                patterns.push(
                    { pattern: `${month}${letterSeparators}${shortYear}`, flags: 'i', priority: 1 } // 11C18 (месяц-год, only exact match)
                );
            }
        }
    }
    
    return patterns;
}

/**
 * Highlight text with search conditions and date patterns
 * Date patterns have priority and are applied first
 * @param {string} text - Text to highlight
 * @param {Array} matchCond - Search conditions from getMatchConditions
 * @param {string} queryOriginal - Original search query (for date recognition)
 * @returns {string} - Highlighted text with <b> tags
 */
export function highlightText(text, matchCond, queryOriginal = '') {
    if (!text || typeof text !== 'string') {
        if (debugMode === '1') console.warn('[text-highlighter] Invalid text provided:', text);
        return text || '';
    }
    
    if (!window.$ || !window.$.each) {
        return text;
    }
    
    let highlightedText = text;
    // Captured up-front so the catch-handler in the date-pattern loop is not in TDZ when it logs.
    const queryChanged = queryOriginal && queryOriginal !== lastHighlightedQuery;

    // Step 1: Extract date patterns (HIGH PRIORITY)
    const datePatterns = generateDateHighlightPatterns(queryOriginal);
    
    // Step 2: Extract text patterns from matchCond (LOWER PRIORITY)
    const textPatterns = extractPatterns(matchCond || []);
    
    // Step 3: Apply date patterns first (higher priority)
    // Sort by priority (higher first) and pattern length (longer first) for better matching
    const sortedDatePatterns = [...datePatterns].sort((a, b) => {
        const aPriority = (typeof a === 'object' && a.priority) ? a.priority : 1;
        const bPriority = (typeof b === 'object' && b.priority) ? b.priority : 1;
        if (aPriority !== bPriority) return bPriority - aPriority;
        const aPattern = typeof a === 'string' ? a : a.pattern;
        const bPattern = typeof b === 'string' ? b : b.pattern;
        return bPattern.length - aPattern.length; // Longer patterns first
    });
    
    for (const datePattern of sortedDatePatterns) {
        try {
            const patternStr = typeof datePattern === 'string' ? datePattern : datePattern.pattern;
            const flags = (typeof datePattern === 'object' && datePattern.flags) ? datePattern.flags : 'i';
            const regex = new RegExp(`(${patternStr})`, flags + 'g');
            highlightedText = highlightedText.replace(regex, '<b>$1</b>');
        } catch (e) {
            if (debugMode === '1' && queryChanged) {
                console.warn('[text-highlighter] Invalid date pattern:', datePattern, e);
            }
        }
    }
    
    // Step 4: Apply text patterns (lower priority)
    // Use a marker approach to avoid double highlighting: mark already highlighted parts
    // Replace <b>...</b> with a temporary marker, apply text patterns, then restore markers
    const markerPrefix = '___HIGHLIGHT_MARKER_';
    const markers = [];
    let markerIndex = 0;
    
    // Step 4a: Replace existing highlights with markers
    highlightedText = highlightedText.replace(/<b>(.*?)<\/b>/g, (match, content) => {
        const marker = `${markerPrefix}${markerIndex}___`;
        markers[markerIndex] = `<b>${content}</b>`;
        markerIndex++;
        return marker;
    });
    
    // Step 4b: Apply text patterns
    for (const { pattern, modifier, flags } of textPatterns) {
        try {
            const patternFlags = (flags !== undefined && flags !== null) ? flags : (modifier || "i");
            const regex = new RegExp(`(${pattern})`, patternFlags + "g");
            highlightedText = highlightedText.replace(regex, '<b>$1</b>');
        } catch (e) {
            if (debugMode === '1') console.warn('[text-highlighter] Invalid text pattern:', pattern, e);
        }
    }
    
    // Step 4c: Restore original highlights (replace markers back)
    for (let i = 0; i < markers.length; i++) {
        const marker = `${markerPrefix}${i}___`;
        highlightedText = highlightedText.replace(marker, markers[i]);
    }
    
    // Deduplication: only log if query changed
    if (debugMode === '1' && queryChanged && datePatterns.length > 0) {
        console.log('[text-highlighter] Applied highlighting:', {
            query: queryOriginal,
            datePatterns: datePatterns.length,
            textPatterns: textPatterns.length,
            datePatternsList: datePatterns.slice(0, 5).map(p => typeof p === 'string' ? p : p.pattern), // Show first 5
            originalLength: text.length,
            highlightedLength: highlightedText.length
        });
        lastHighlightedQuery = queryOriginal;
    }
    
    return highlightedText;
}

// Global export for backward compatibility
window.highlightText = highlightText;

